import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowRight, Linkedin, Twitter } from 'lucide-react';

export function Footer() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast({ title: 'Please enter a valid email', variant: 'destructive' });
      return;
    }
    setLoading(true);
    let error: any = null;
    try {
      const res = await supabase.from('newsletter_subscribers' as any).insert({ email: trimmed } as any);
      error = res.error;
    } catch (e) {
      error = e;
    }
    setLoading(false);
    if (error?.code === '23505') {
      toast({ title: "You're already subscribed!" });
    } else if (error) {
      toast({ title: 'Something went wrong', variant: 'destructive' });
    } else {
      toast({ title: '🎉 Subscribed! Weekly ROI insights incoming.' });
      setEmail('');
    }
  };

  return (
    <footer className="border-t border-border bg-card mt-10 sm:mt-16">
      <div className="container mx-auto px-3 sm:px-4 py-8 sm:py-10">
        {/* Newsletter */}
        <div className="mb-10 pb-8 border-b border-border">
          <div className="max-w-md mx-auto text-center">
            <h3 className="font-display font-bold text-foreground mb-1">Get Weekly ROI Insights</h3>
            <p className="text-sm text-muted-foreground mb-4">Top-performing tools, ROI benchmarks, and software comparisons — every Thursday.</p>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <Input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 h-9 text-sm"
                required
              />
              <Button type="submit" size="sm" className="gap-1 h-9 shrink-0" disabled={loading}>
                Subscribe <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </form>
          </div>
        </div>

        {/* SEO keyword-rich text block */}
        <p className="text-xs text-muted-foreground max-w-3xl mx-auto text-center mb-8">
          TrustROI is the best platform to compare software tools ranked by ROI. Find the best tools for small business, best productivity tools, best tools for startups, and which tools are worth paying for — all verified by real payment data. Compare software worth the cost with unbiased reviews and ROI scores. Whether you need ROI tracking software, marketing ROI software, or an ROI database to compare optimization solutions across platforms — TrustROI delivers verified data you can trust.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div>
            <h3 className="font-display font-bold text-sm text-foreground mb-3">Best Tools</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">Best Tools Ranked by ROI</Link></li>
              <li><Link to="/categories" className="text-muted-foreground hover:text-foreground transition-colors">Software Comparison</Link></li>
              <li><Link to="/blog/how-to-calculate-saas-roi" className="text-muted-foreground hover:text-foreground transition-colors">Software ROI Calculator</Link></li>
              <li><Link to="/blog/best-crm-for-small-teams-2026-what-the-retention-data-shows-vs-what-the-reviews-say" className="text-muted-foreground hover:text-foreground transition-colors">Best CRM Software</Link></li>
              <li><Link to="/blog/best-project-management-tools-2026-ranked-by-verified-retention-not-marketing-spend" className="text-muted-foreground hover:text-foreground transition-colors">Best Project Management Software</Link></li>
              <li><Link to="/categories" className="text-muted-foreground hover:text-foreground transition-colors">ROI Tracking Tools</Link></li>
              <li><Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">ROI Software</Link></li>
              <li><Link to="/trustwall" className="text-muted-foreground hover:text-foreground transition-colors">Collect Verified Testimonials</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-display font-bold text-sm text-foreground mb-3">For Builders</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/add" className="text-muted-foreground hover:text-foreground transition-colors">Submit Your Tool</Link></li>
              <li><Link to="/launchpad" className="text-muted-foreground hover:text-foreground transition-colors">Launchpad — List Free</Link></li>
              <li><Link to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors">Get Featured</Link></li>
              <li><Link to="/blog/where-to-list-saas-2026" className="text-muted-foreground hover:text-foreground transition-colors">Startup Directories 2026</Link></li>
              <li><Link to="/blog/how-to-calculate-saas-roi" className="text-muted-foreground hover:text-foreground transition-colors">How to Calculate ROI</Link></li>
              <li><Link to="/categories" className="text-muted-foreground hover:text-foreground transition-colors">Best Marketing Automation Tools</Link></li>
              <li><Link to="/blog/email-marketing-tools-ranked-by-return-on-investment-2026-trustroi-guide" className="text-muted-foreground hover:text-foreground transition-colors">Marketing ROI Software</Link></li>
              <li><Link to="/stack-builder" className="text-muted-foreground hover:text-foreground transition-colors">Plan It ROI — Stack Builder</Link></li>
              <li><Link to="/trustwall" className="text-muted-foreground hover:text-foreground transition-colors">TrustWall — Verified Testimonials</Link></li>
              <li><Link to="/trustwall/setup" className="text-muted-foreground hover:text-foreground transition-colors">TrustWall — Verified Reviews</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-display font-bold text-sm text-foreground mb-3">Compare</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/alternatives" className="text-muted-foreground hover:text-foreground transition-colors">All Alternatives</Link></li>
              <li><Link to="/alternatives/producthunt" className="text-muted-foreground hover:text-foreground transition-colors">vs Product Hunt</Link></li>
              <li><Link to="/alternatives/g2" className="text-muted-foreground hover:text-foreground transition-colors">vs G2</Link></li>
              <li><Link to="/alternatives/capterra" className="text-muted-foreground hover:text-foreground transition-colors">vs Capterra</Link></li>
              <li><Link to="/alternatives/betalist" className="text-muted-foreground hover:text-foreground transition-colors">vs BetaList</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="font-display font-bold text-sm text-foreground mb-3">TrustROI</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors">About TrustROI</Link></li>
              <li><Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">TrustROI Software Rankings</Link></li>
              <li><Link to="/about" className="text-muted-foreground hover:text-foreground transition-colors">Unbiased Software Reviews</Link></li>
              <li><Link to="/blog" className="text-muted-foreground hover:text-foreground transition-colors">Blog</Link></li>
              <li><Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">ROI CRM Rankings</Link></li>
              <li><Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">Online ROI Database</Link></li>
              <li><Link to="/categories" className="text-muted-foreground hover:text-foreground transition-colors">Compare ROI Across Tools</Link></li>
            </ul>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <p>© {new Date().getFullYear()} TrustROI — Best software tools ranked by ROI. Unbiased software comparison.</p>
          <div className="flex items-center gap-3">
            <a href="https://www.linkedin.com/company/trustroi/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="TrustROI on LinkedIn">
              <Linkedin className="h-4 w-4" />
            </a>
            <a href="https://x.com/trustroi_online" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="TrustROI on X">
              <Twitter className="h-4 w-4" />
            </a>
            <a href="https://www.reddit.com/r/TrustROI/" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors" aria-label="TrustROI on Reddit">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm6.066 13.71c.147.422.22.864.22 1.317 0 2.718-3.095 4.925-6.912 4.925s-6.912-2.207-6.912-4.925c0-.453.073-.895.22-1.317a1.643 1.643 0 01-.59-1.266 1.655 1.655 0 012.81-1.181c1.251-.86 2.945-1.4 4.84-1.46l.93-4.344a.372.372 0 01.443-.283l3.074.654a1.166 1.166 0 012.14.578 1.166 1.166 0 01-1.166 1.166 1.167 1.167 0 01-1.136-.907l-2.716-.579-.822 3.842c1.843.076 3.484.616 4.705 1.46a1.654 1.654 0 012.81 1.182c0 .493-.218.95-.59 1.266zm-9.573.668a1.166 1.166 0 100 2.332 1.166 1.166 0 000-2.332zm5.014 0a1.166 1.166 0 100 2.332 1.166 1.166 0 000-2.332zm-4.803 3.459c-.043-.04-.043-.107 0-.15a.107.107 0 01.15 0c.85.81 2.014 1.205 3.146 1.205s2.296-.394 3.146-1.205a.107.107 0 01.15 0c.043.043.043.11 0 .15-.893.857-2.102 1.286-3.296 1.286s-2.403-.43-3.296-1.286z"/>
              </svg>
            </a>
            <span className="hidden sm:inline">·</span>
            <p className="hidden sm:inline">ROI data verified by TrustROI. <Link to="/about" className="underline hover:text-foreground">Learn more</Link></p>
          </div>
        </div>
      </div>
    </footer>
  );
}